/**
 * Phone number with a country picker: flag + dial code, then the national number.
 * Emits E.164 (`+66812345678`) — what `phone_verification` and MessageEagle
 * require — so an applicant types `081 234 5678` the way they always write it.
 * Pasting a full `+…` number into the field switches the country to match it.
 * The list itself (search, keyboard, phone-sized sheet) is ./CountryPicker.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Chevrons, CountryPicker } from './CountryPicker';
import { countries, countryOf, DEFAULT_COUNTRY, flagUrl, splitE164, toE164 } from './phoneNumber';

interface PhoneInputProps {
  /** The E.164 value, or '' when empty. */
  value: string;
  onChange: (e164: string) => void;
  /** Accessible name of the number field (the visible label lives with the caller). */
  label: string;
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  defaultCountry?: string;
}

export function PhoneInput({
  value,
  onChange,
  label,
  id,
  name,
  placeholder,
  disabled,
  defaultCountry = DEFAULT_COUNTRY,
}: PhoneInputProps) {
  const { t, i18n } = useTranslation();
  const list = useMemo(() => countries(i18n.language), [i18n.language]);
  const initial = splitE164(value, list);
  const [code, setCode] = useState(initial?.code ?? defaultCountry);
  const [national, setNational] = useState(initial?.national ?? '');
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  const country = countryOf(list, code);
  const emit = (dial: string, digits: string) => onChange(toE164(dial, digits));

  const choose = (next: string) => {
    setCode(next);
    setOpen(false);
    emit(countryOf(list, next).dial, national);
  };

  const typeNumber = (raw: string) => {
    // A pasted international number carries its own country.
    const split = raw.trim().startsWith('+') ? splitE164(raw, list) : null;
    if (split) {
      setCode(split.code);
      setNational(split.national);
      emit(countryOf(list, split.code).dial, split.national);
      return;
    }
    setNational(raw);
    emit(country.dial, raw);
  };

  return (
    <div className="phone-input" ref={root}>
      <div className="phone-input__field">
        <button
          type="button"
          className="phone-input__country"
          data-track="dial_code_selector"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t('flow.phoneInput.country', { name: country.name, dial: country.dial })}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          <img src={flagUrl(country.code)} alt="" width={22} height={22} />
          <Chevrons />
        </button>
        <span className="phone-input__dial" aria-hidden="true">
          {country.dial}
        </span>
        <input
          id={id}
          name={name}
          className="phone-input__number"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          aria-label={label}
          placeholder={placeholder}
          disabled={disabled}
          value={national}
          onChange={(event) => typeNumber(event.target.value)}
        />
      </div>
      <CountryPicker
        open={open}
        onClose={close}
        selected={code}
        onChoose={choose}
        anchor={root}
        showDial
        title={t('flow.phoneInput.title')}
      />
    </div>
  );
}
